import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import { subscribeCategories } from '../services/categories';
import { subscribeTasks, createTask, updateTask, deleteTask } from '../services/tasks';
import Modal from '../components/Modal';

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Subscribe tasks using service
  useEffect(() => {
    const unsub = subscribeTasks(
      (list) => setTasks(list),
      (err) => setError(err?.message || 'Failed to load tasks')
    );
    return () => unsub();
  }, []);

  // Subscribe categories for selector
  useEffect(() => {
    const unsub = subscribeCategories(
      (list) => setCategories(list),
      (err) => setError(err?.message || 'Failed to load categories')
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => {
      const name = (t.name || '').toLowerCase();
      const desc = (t.description || '').toLowerCase();
      const id = (t.id || '').toLowerCase();
      const catName = (categories.find((c) => c.id === t.categoryId)?.name || '').toLowerCase();
      return name.includes(q) || desc.includes(q) || id.includes(q) || catName.includes(q);
    });
  }, [tasks, categories, search]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const getVal = (t) => {
      switch (sortKey) {
        case 'name':
          return (t.name || '').toLowerCase();
        case 'description':
          return (t.description || '').toLowerCase();
        case 'category': {
          const cat = categories.find((c) => c.id === t.categoryId)?.name || '';
          return cat.toLowerCase();
        }
        case 'createdAt':
        default: {
          const v = t.createdAt;
          return v?.toMillis ? v.toMillis() : (typeof v === 'number' ? v : (typeof v === 'string' ? Date.parse(v) || 0 : 0));
        }
      }
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, categories, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const formatDate = (v) => {
    if (!v) return '—';
    try {
      const date =
        v?.toMillis ? new Date(v.toMillis()) :
        typeof v === 'number' ? new Date(v) :
        typeof v === 'string' ? new Date(v) : null;

      if (!date || isNaN(date.getTime())) return String(v);

      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');

    if (!name.trim()) {
      setStatus('idle');
      setError('Please enter a name.');
      return;
    }
    if (!description.trim()) {
      setStatus('idle');
      setError('Please enter a description.');
      return;
    }
    if (!categoryId) {
      setStatus('idle');
      setError('Please select a category.');
      return;
    }

    try {
      await createTask({
        name,
        description,
        categoryId,
      });
      setName('');
      setDescription('');
      setCategoryId('');
      setIsModalOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to add task');
    } finally {
      setStatus('idle');
    }
  };
// Start editing an existing task
const startEdit = (task) => {
  setEditingTaskId(task.id);
  setName(task.name || '');
  setDescription(task.description || '');
  setCategoryId(task.categoryId || '');
  setError('');
  setIsEditModalOpen(true);
};

// Cancel editing and reset fields
const cancelEdit = () => {
  setIsEditModalOpen(false);
  setEditingTaskId(null);
  setName('');
  setDescription('');
  setCategoryId('');
};

// Save updates to an existing task
const handleUpdate = async (e) => {
  e.preventDefault();
  if (!editingTaskId) return;
  setStatus('loading');
  setError('');
  if (!name.trim()) { setStatus('idle'); setError('Please enter a name.'); return; }
  if (!description.trim()) { setStatus('idle'); setError('Please enter a description.'); return; }
  if (!categoryId) { setStatus('idle'); setError('Please select a category.'); return; }
  try {
    await updateTask(editingTaskId, { name, description, categoryId });
    setIsEditModalOpen(false);
    setEditingTaskId(null);
    setName('');
    setDescription('');
    setCategoryId('');
  } catch (err) {
    setError(err.message || 'Failed to update task');
  } finally {
    setStatus('idle');
  }
};

// Delete a task with confirmation
const handleDelete = async (task) => {
  if (!task?.id) return;
  const ok = window.confirm('Delete this task? This cannot be undone.');
  if (!ok) return;
  setStatus('loading');
  setError('');
  try {
    await deleteTask(task.id);
  } catch (err) {
    setError(err?.message || 'Failed to delete task');
  } finally {
    setStatus('idle');
  }
};

  return (
    <AdminLayout>
      <div className="content-card">
        <h2 className="section-title">Task Creation</h2>
        <p className="section-subtitle">Create and view tasks.</p>
        <div className="table-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <input
            className="input"
            type="text"
            placeholder="Search by name, description, or category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 280, maxWidth: 360, flex: '0 1 360px' }}
          />
          <button className="btn btn-pill btn-dark" type="button" onClick={() => setIsModalOpen(true)}>Add Task</button>
        </div>
        <Modal open={isModalOpen} title="Add Task" onClose={() => setIsModalOpen(false)}
          footer={(
            <>
              <button type="button" className="btn btn-pill btn-accent" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="btn btn-pill btn-dark" type="submit" form="taskForm" disabled={status === 'loading'}>
                {status === 'loading' ? 'Saving…' : 'Save Task'}
              </button>
            </>
          )}
        >
          <form id="taskForm" onSubmit={handleAdd} className="form-row full" style={{ maxWidth: 640 }}>
            <input data-autofocus className="input" type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <textarea className="input textarea" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} required />
            <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {error && <p style={{ color: '#c0392b' }}>{error}</p>}
          </form>
        </Modal>
        <Modal open={isEditModalOpen} title="Edit Task" onClose={cancelEdit}
          footer={(
            <>
              <button type="button" className="btn btn-pill btn-accent" onClick={cancelEdit}>Cancel</button>
              <button className="btn btn-pill btn-dark" type="submit" form="taskEditForm" disabled={status === 'loading'}>
                {status === 'loading' ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          )}
        >
          <form id="taskEditForm" onSubmit={handleUpdate} className="form-row full" style={{ maxWidth: 640 }}>
            <input data-autofocus className="input" type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <textarea className="input textarea" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} required />
            <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {error && <p style={{ color: '#c0392b' }}>{error}</p>}
          </form>
        </Modal>
        <div style={{ marginTop: 16 }}>
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button type="button" onClick={() => toggleSort('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Name {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('description')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Description {sortKey === 'description' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('category')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Category {sortKey === 'category' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('createdAt')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Created {sortKey === 'createdAt' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
               <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.id}>
                  <td>{t.name || '—'}</td>
                  <td>{t.description || '—'}</td>
                  <td>{categories.find((c) => c.id === t.categoryId)?.name || '—'}</td>
                  <td>{formatDate(t.createdAt)}</td>
                 <td>
                   <button className="btn btn-pill btn-dark" type="button" onClick={() => startEdit(t)} style={{ marginRight: 8 }}>Edit</button>
                   <button className="btn btn-pill btn-accent" type="button" onClick={() => handleDelete(t)}>Delete</button>
                 </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}